import sprites from './assets/table-sounds.json'
import type { TableSoundKind } from '@v2/contracts'

export type InterfaceSound = 'hover' | 'select' | 'confirm' | 'cancel' | 'error'
export type PhysicalInteractionSound = 'card-hover' | 'card-pickup' | 'piece-hover' | 'pickup'
export type RecordedSound = TableSoundKind | InterfaceSound | PhysicalInteractionSound
type SpriteName = keyof typeof sprites

const variants: Record<RecordedSound, readonly SpriteName[]> = {
  hover: ['wood-cursor-1'], select: ['wood-select-1'], confirm: ['wood-confirm-1'],
  cancel: ['wood-cancel-2', 'wood-cancel-1'], error: ['wood-error-1'],
  deal: ['v1-card-slide-5', 'v1-card-slide-6', 'v1-card-slide-7', 'v1-card-slide-8'],
  shuffle: ['v1-card-slide-6'],
  place: ['v1-card-place-1', 'v1-card-place-2', 'v1-card-place-4'],
  'card-hover': ['v1-card-slide-2', 'v1-card-slide-3', 'v1-card-slide-4'],
  'card-pickup': ['v1-card-slide-5', 'v1-card-slide-6'],
  'piece-hover': ['wood-select-1'],
  token: ['table-piece-impact-1', 'table-piece-impact-2'],
  reveal: ['v1-card-place-1'], pickup: ['table-piece-move-2', 'table-piece-move-1'],
}

/** V1 card levels account for the 0.34 master gain; other sources have matched peaks. */
const levels: Record<RecordedSound, number> = {
  hover: 0.04, select: 0.22, confirm: 0.24, cancel: 0.18, error: 0.18,
  'card-hover': 0.02 / 0.34, 'card-pickup': 0.08 / 0.34, 'piece-hover': 0.065,
  deal: 0.16 / 0.34, shuffle: 0.13 / 0.34, place: 0.16 / 0.34, token: 0.32, reveal: 0.13 / 0.34, pickup: 0.18,
}

export function soundSlice(sound: RecordedSound, variation: number) {
  const names = variants[sound]
  const [offset, length] = sprites[names[variation % names.length]]
  const maximum = sound === 'hover' ? 0.1 : sound === 'piece-hover' ? 0.12 : length
  const hovering = sound === 'hover' || sound === 'piece-hover'
  const originalCard = names[0].startsWith('v1-')
  return { offset, duration: Math.min(length, maximum), gain: levels[sound],
    attack: originalCard ? 0 : hovering ? 0.015 : 0.004, release: originalCard ? 0 : hovering ? 0.06 : 0.02,
    lowpass: sound === 'piece-hover' ? 1800 : sound === 'hover' ? 2600 : null,
  }
}

export async function loadSoundBank(context: AudioContext, signal: AbortSignal): Promise<AudioBuffer> {
  const response = await fetch(new URL('./assets/table-sounds.wav', import.meta.url), { signal })
  if (!response.ok) throw new Error('Soundbank unavailable')
  return context.decodeAudioData(await response.arrayBuffer())
}
